"use client";

import { useEffect, useMemo, useState } from "react";

import { LADDER_SHAPE, asAnalysis, fetchRung, type Branch, type Level } from "@/lib/levels/client";
import { contractOf, describe, rungWords } from "@/lib/levels/words";
import type { AnalysisResult } from "@/lib/music/analyzer-types";

/**
 * Banc d'essai du programme.
 *
 * Il répond à une question et il faut lui garder cette étroitesse : **est-ce
 * que ça sonne juste, et est-ce que ça se joue ?** Pas d'états, pas de
 * validation, pas de progression.
 *
 * Une seule liste de niveaux, dans l'ordre. Une version antérieure affichait
 * les deux moitiés comme deux boutons côte à côte, et c'était faux : ça se
 * lisait comme un choix entre deux méthodes, alors que le modèle seul n'atteint
 * jamais le morceau et que la partition seule n'enseigne rien de transportable.
 *
 * Ce panneau ne calcule plus rien. Il demande l'échelle à l'analyseur et met
 * ses faits en français.
 */

interface LevelLabSidebarProps {
  /** L'analyse du morceau chargé, ou `null` tant qu'aucun n'est ouvert. */
  analysis: AnalysisResult | null;
  /** Remplace le morceau joué. Le studio possède la recette, pas ce panneau. */
  onApply: (analysis: AnalysisResult) => Promise<void>;
  busy: boolean;
}

const BRANCHES: { id: Branch; label: string }[] = [
  { id: "harmonique", label: "Par l'harmonie" },
  { id: "par-coeur", label: "Par cœur" },
];

export function LevelLabSidebar({ analysis, onApply, busy }: LevelLabSidebarProps) {
  const [branch, setBranch] = useState<Branch>("harmonique");
  const [step, setStep] = useState(0);
  /**
   * Les niveaux déjà demandés, par identifiant.
   *
   * On n'en fabrique pas dix pour en jouer un. Un niveau seul coûte le
   * dixième de l'échelle entière (0,3 à 0,7 seconde contre 3,7 sur la
   * *Comptine*), parce que le solveur de doigté passe une fois au lieu de dix,
   * et ce qui a été demandé une fois n'est pas redemandé.
   */
  const [built, setBuilt] = useState<Record<string, Level>>({});
  /**
   * L'échec, et le niveau qu'il concerne.
   *
   * Un message d'erreur sans son niveau survit au clic suivant et accuse le
   * mauvais : c'est le niveau 4 qui n'a pas répondu, pas le 5 qu'on regarde
   * maintenant.
   */
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);

  const shape = LADDER_SHAPE[branch];
  const rungId = shape[Math.min(step, shape.length - 1)];
  const key = `${branch}:${rungId}`;
  const current = built[key] ?? null;

  // Un morceau qui change vide ce qui a été fabriqué pour le précédent.
  //
  // Ajusté pendant le rendu et non dans un effet : c'est ce que React
  // recommande pour un état qui dérive d'une prop, et un effet ferait un rendu
  // de plus avec les niveaux de l'ancien morceau à l'écran.
  const [openedFor, setOpenedFor] = useState(analysis);
  if (analysis !== openedFor) {
    setOpenedFor(analysis);
    setBuilt({});
    setFailure(null);
    setStep(0);
  }

  // Déduit, pas gardé : on charge exactement quand le niveau regardé n'est ni
  // fabriqué ni en échec. Un état de plus serait un état à remettre à zéro.
  const loading = analysis !== null && current === null && failure?.key !== key;
  const error = failure?.key === key ? failure.message : null;

  useEffect(() => {
    if (!analysis || built[key] !== undefined) {
      return;
    }

    let abandoned = false;

    fetchRung(analysis, branch, shape.indexOf(rungId))
      .then((level) => {
        // Un clic plus rapide que la réponse : le niveau qui revient n'est
        // plus celui qu'on regarde, mais il est bon à garder.
        if (!abandoned) {
          setBuilt((held) => ({ ...held, [key]: level }));
        }
      })
      .catch((e: unknown) => {
        if (!abandoned) {
          setFailure({ key, message: e instanceof Error ? e.message : String(e) });
        }
      });

    return () => {
      abandoned = true;
    };
  }, [analysis, branch, rungId, shape, built, key]);

  /**
   * Ce que le niveau apporte, regroupé par ce qu'il apporte.
   *
   * Une observation par fenêtre d'accord serait illisible sur un morceau de
   * cent trente accords. Regrouper par nature garde la phrase et remplace la
   * mesure par un compte, et la liste des mesures reste sous la main pour le
   * jour où le parcours travaillera phrase par phrase.
   */
  const news = useMemo(() => {
    if (!current) {
      return [];
    }

    const byKind = new Map<string, { sentence: string; measures: number[] }>();

    for (const o of current.news) {
      const sentence = describe(o);
      const held = byKind.get(sentence) ?? { sentence, measures: [] };

      held.measures.push(o.measure);
      byKind.set(sentence, held);
    }

    return [...byKind.values()].sort((a, b) => b.measures.length - a.measures.length);
  }, [current]);

  if (!analysis) {
    return <div className="p-4 text-[13px] text-text-muted">Ouvre un morceau, puis reviens ici.</div>;
  }

  const chords = analysis.chords ?? [];
  const loop = analysis.harmonicLoop;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto p-4">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-wider text-text-muted">Banc d&apos;essai</div>
        <div className="mt-1 text-[12px] text-text-muted">
          Chaque niveau garde ce qui a été compris, même quand la main change de place.
        </div>
      </div>

      {/* La carte : l'étape de préparation, qui n'ajoute aucune note. */}
      <div className="rounded-lg border border-white/[0.06] p-2.5 text-[11px] leading-relaxed text-text-muted">
        <div className="mb-1 font-medium text-text">La carte</div>
        <div>
          {analysis.key.label}, {analysis.tempo.beatsPerMeasure} temps par mesure, {analysis.tempo.defaultBpm} à la
          noire.
        </div>
        <div>
          {analysis.sections.length} sections ({[...new Set(analysis.sections.map((s) => s.type))].join(", ")}),{" "}
          {analysis.phrases.count} phrases, {chords.length} accords, {analysis.notes.length} notes.
        </div>
        {loop ? (
          <div>
            Boucle de {loop.measures} mesures : <b>{loop.chords.join(" ")}</b>, {loop.turns} tours, accord{" "}
            {loop.agreement.toFixed(2)}.
          </div>
        ) : (
          <div>Aucune boucle harmonique fiable : le découpage se fera par section.</div>
        )}
      </div>

      <div className="flex gap-1">
        {BRANCHES.map((b) => (
          <button
            key={b.id}
            onClick={() => setBranch(b.id)}
            className="flex-1 rounded-lg px-2 py-1.5 text-[12px] font-medium transition-colors"
            style={{
              backgroundColor: branch === b.id ? "rgba(139,92,246,0.10)" : "rgba(255,255,255,0.015)",
              border: `1px solid ${branch === b.id ? "rgba(139,92,246,0.30)" : "rgba(255,255,255,0.04)"}`,
            }}
          >
            {b.label}
          </button>
        ))}
      </div>

      {error && (
        <div
          className="rounded-lg px-2.5 py-2 text-[11px] leading-relaxed"
          style={{ backgroundColor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}
        >
          {error}
        </div>
      )}

      {loading && <div className="text-[12px] text-text-muted">L&apos;analyseur calcule l&apos;échelle…</div>}

      <div className="flex flex-col gap-1">
        {shape.map((id, i) => {
          const words = rungWords(id);
          const level = built[`${branch}:${id}`] ?? null;
          const silent = level !== null && i > 0 && level.news.length === 0;
          const seam = i > 0 && built[`${branch}:${shape[i - 1]}`]?.realisation === "modele" && level?.realisation === "partition";

          return (
            <div key={id} className="flex flex-col gap-1">
              {seam && (
                <div
                  className="mt-1 rounded-lg px-2.5 py-1.5 text-[10px] leading-relaxed"
                  style={{ backgroundColor: "rgba(234,179,8,0.07)", border: "1px solid rgba(234,179,8,0.22)" }}
                >
                  Ici on change <b>une main à la fois</b>. La gauche prend la partition, la droite garde
                  l&apos;accord qu&apos;elle tient depuis le premier niveau. Cet échafaudage tombe au niveau où la
                  partition donne enfin quelque chose à la main droite.
                </div>
              )}

              <button
                onClick={() => setStep(i)}
                className="flex flex-col items-start rounded-lg px-2.5 py-2 text-left transition-colors"
                style={{
                  backgroundColor: step === i ? "rgba(139,92,246,0.10)" : "rgba(255,255,255,0.015)",
                  border: `1px solid ${step === i ? "rgba(139,92,246,0.30)" : "rgba(255,255,255,0.04)"}`,
                  opacity: silent ? 0.5 : 1,
                }}
              >
                <span className="flex w-full items-baseline justify-between gap-2">
                  <span className="text-[13px] font-medium">
                    {i + 1}. {words.label}
                  </span>
                  <span className="tabular-nums text-[11px] text-text-muted">
                    {level === null ? "—" : silent ? "rien de neuf ici" : `${level.notes.length} notes`}
                  </span>
                </span>
                <span className="text-[11px] text-text-muted">
                  {words.summary}
                  {level?.scaffolded && " · main droite encore au modèle"}
                </span>
                {step === i && (
                  <span className="mt-1 text-[10px] leading-relaxed text-text-muted">
                    Ce qui reste acquis : {words.concepts.join(" · ")}.
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Ce que le niveau apporte, en français, sur ce morceau. */}
      {current && news.length > 0 && (
        <div className="rounded-lg border border-white/[0.06] p-2.5 text-[11px] leading-relaxed">
          <div className="mb-1 font-medium">Ce qui est nouveau</div>
          <ul className="flex flex-col gap-1 text-text-muted">
            {news.map((n) => (
              <li key={n.sentence}>
                {n.sentence}{" "}
                <span className="tabular-nums opacity-60">
                  ({n.measures.length === 1 ? `mesure ${n.measures[0]}` : `${n.measures.length} fois`})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {current && (
        <div
          className="rounded-lg px-2.5 py-2 text-[11px] leading-relaxed"
          style={{
            backgroundColor: current.realisation === "modele" ? "rgba(234,179,8,0.07)" : "rgba(34,197,94,0.06)",
            border: `1px solid ${current.realisation === "modele" ? "rgba(234,179,8,0.22)" : "rgba(34,197,94,0.20)"}`,
          }}
        >
          {contractOf(current)}
        </div>
      )}

      <button
        onClick={() => current && analysis && onApply(asAnalysis(analysis, current))}
        disabled={busy || !current}
        className="rounded-lg px-3 py-2 text-[13px] font-medium"
        style={{
          backgroundColor: "rgba(139,92,246,0.18)",
          border: "1px solid rgba(139,92,246,0.35)",
          opacity: busy || !current ? 0.5 : 1,
        }}
      >
        {busy ? "Chargement…" : `Charger le niveau ${step + 1} sur ${shape.length}`}
      </button>
    </div>
  );
}
